// dependencies
import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import dynamic from "next/dynamic";

// components
import Drawer, {
  DrawerHeader,
} from "@cloudspire/legacy-resources/src/components/Drawer";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Portal from "@cloudspire/legacy-resources/src/components/Portal";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import RoomCard, {
  RoomCardPlaceholder,
} from "@cloudspire/legacy-resources/src/components/RoomCard";

// containers
import NoResultsContainer from "./NoResults";

// contexts
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// helpers
import { roomHasMap } from "@cloudspire/legacy-resources/src/helpers/room";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";

const Map = dynamic(() => import("./Map"), { ssr: false });

function RoomListResults(props) {
  const {
    showMap,
    perPage,
    onlyButtonLoading,
    context,
    roomDetailQuery,
    roomsResponse,
    onChangedViewport,
    onChangePagination,
    onPressShowMap,
  } = props;

  const intl = useIntl();

  const roomList = roomsResponse.data?.roomList ?? [];

  const { frontRouter } = useRouters();

  const { matches: matchesXl } = useViewportMedia({
    queries: [{ minWidth: 1800 }],
  });
  const { matches: matchesLg } = useViewportMedia({
    queries: [{ minWidth: 1450 }],
  });
  const { matches: matchesMd } = useViewportMedia({
    queries: [{ minWidth: 1000 }],
  });
  const { matches: matchesSm } = useViewportMedia({
    queries: [{ minWidth: 605 }],
  });

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (matchesXl || matchesLg) {
      onChangePagination({ perPage: 15 });
    } else if (matchesMd) {
      onChangePagination({ perPage: 12 });
    } else if (matchesSm) {
      onChangePagination({ perPage: 12 });
    }
  }, [matchesXl, matchesLg, matchesMd, matchesSm]);

  const [hoveredRoomReference, setHoveredRoomReference] = useState(null);

  const handleHoverRoom = ({ roomReference }) => {
    setHoveredRoomReference(roomReference);
  };

  function handlePress(/*{ roomReference, context }*/) {
    // TODO: GTM : ACTION
    // @magsout si tu veux les query-params, tu peux utiliser window.location.search (ça te retourne une string; ex: ?checkin=2020-06-20&checkout=2020-06-27)
    //   si tu en as besoin parser, tu peux utiliser: `import { parseQuery } from "@cloudspire/legacy-shared/src/libraries"`
  }

  const $map = (
    <Map
      markerList={roomList.reduce((markerList, room) => {
        if (roomHasMap({ room })) {
          markerList.push({
            id: room.reference,
            type: hoveredRoomReference === room.reference ? "brand" : "classic",
            position: [room.lat, room.lng],
            component: (
              <RoomCard
                flux="block"
                context={context}
                room={room}
                pricing={roomsResponse.data?.priceList[room.reference]}
                linkProps={{
                  gtm: "room-card",
                }}
                to={generateUri({
                  router: frontRouter,
                  name: "App.Action.RoomWithSlug",
                  parameters: {
                    lang: intl.locale,
                    slug: room.slug,
                  },
                })}
                onPress={handlePress}
              />
            ),
          });
        }

        return markerList;
      }, [])}
      onChangedViewport={onChangedViewport}
    />
  );

  const l1MaxColumns = ({
    matchesXl,
    matchesLg,
    matchesMd,
    matchesSm,
    showMap,
  }) => {
    if (matchesXl || matchesLg || matchesMd || matchesSm) {
      if (showMap) {
        return 2;
      } else {
        return 1;
      }
    }

    return 1;
  };

  const l2MaxColumns = ({
    matchesXl,
    matchesLg,
    matchesMd,
    matchesSm,
    showMap,
  }) => {
    if (matchesXl || matchesLg) {
      if (showMap) {
        return 2;
      } else {
        return 5;
      }
    }

    if (matchesMd) {
      if (showMap) {
        return 2;
      } else {
        return 4;
      }
    }

    if (matchesSm) {
      if (showMap) {
        return 1;
      } else {
        return 2;
      }
    }

    return 1;
  };

  return (
    <>
      <Portal selector="body">
        <Drawer
          contentStyle={{ backgroundColor: "#fff", width: "100%" }}
          isOpened={!matchesSm && showMap}
          onShouldClose={() => onPressShowMap(false)}
        >
          {!matchesSm && showMap && (
            <>
              <DrawerHeader
                appearance="classic"
                onPressClose={() => onPressShowMap(false)}
              >
                {intl.formatMessage({
                  defaultMessage: "Voir la carte",
                })}
              </DrawerHeader>

              <Stack marginTop={1} />

              {$map}
            </>
          )}
        </Drawer>
      </Portal>
      <Row
        type="cell"
        maxColumns={l1MaxColumns({
          matchesXl,
          matchesLg,
          matchesMd,
          matchesSm,
          showMap,
        })}
        gutter={0.5}
      >
        <Col>
          <Row
            type="cell"
            gutter={0.5}
            maxColumns={l2MaxColumns({
              matchesXl,
              matchesLg,
              matchesMd,
              matchesSm,
              showMap,
            })}
          >
            {roomList.length > 0 && (
              <>
                {roomList.map((room) => {
                  return (
                    <Col key={room.reference}>
                      <div
                        style={{ height: "100%" }}
                        onMouseOver={() =>
                          handleHoverRoom({ roomReference: room.reference })
                        }
                      >
                        <RoomCard
                          flux="block"
                          context={context}
                          room={room}
                          pricing={
                            roomsResponse.data?.priceList[room.reference]
                          }
                          linkProps={{
                            "data-gtm": "room-card",
                          }}
                          to={generateUri({
                            router: frontRouter,
                            name: "App.Action.RoomWithSlug",
                            parameters: {
                              lang: intl.locale,
                              slug: room.slug,
                            },
                            query: roomDetailQuery,
                          })}
                          shouldRenderCarouselNavigation={false}
                          onPress={handlePress}
                        />
                      </div>
                    </Col>
                  );
                })}
              </>
            )}

            {!onlyButtonLoading &&
              roomsResponse.isValidating &&
              Array(perPage)
                .fill(null)
                .map((_, index) => (
                  <Col key={index}>
                    <RoomCardPlaceholder />
                  </Col>
                ))}

            {!roomsResponse.isValidating && roomList.length === 0 && (
              <NoResultsContainer />
            )}
          </Row>
        </Col>

        {matchesSm && showMap && <Col>{$map}</Col>}
      </Row>
    </>
  );
}

export default RoomListResults;
